import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Table, Popconfirm, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import styles from '../styles';
import { getStyles, clearStyles } from '../Actions';
import { errorNotification, successNotification } from '../Snackbar';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Api from '../Api';
import constants from '../constants';
import Breadcrumbs from '../Components/Breadcrumbs';

const StyleList = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const styleList = useSelector(state => state.styleList);

  useEffect(() => {
    dispatch(getStyles());
    return () => {
      dispatch(clearStyles());
    };
  }, []);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/styles/${id}`);
      successNotification('Style deleted');
      dispatch(getStyles());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateStyle) ? (
            <Tooltip placement="bottom" title="Update">
              <Link to={`/styles/update/${record.id}`}>
                <EditOutlined className={classes.tableIcon} />
              </Link>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteStyle) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
        sorter: sorter('customer'),
        onFilter: onFilter('customer'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: sorter('name'),
        onFilter: onFilter('name'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        sorter: sorter('description'),
        onFilter: onFilter('description'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Length',
        dataIndex: 'length',
        sorter: sorter('length'),
        onFilter: onFilter('length'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Width',
        dataIndex: 'width',
        sorter: sorter('width'),
        onFilter: onFilter('width'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Height',
        dataIndex: 'height',
        sorter: sorter('height'),
        onFilter: onFilter('height'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Diameter',
        dataIndex: 'diameter',
        sorter: sorter('diameter'),
        onFilter: onFilter('diameter'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Flap',
        dataIndex: 'flap',
        sorter: sorter('flap'),
        onFilter: onFilter('flap'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'GSM',
        dataIndex: 'gsm',
        sorter: sorter('gsm'),
        onFilter: onFilter('gsm'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        sorter: sorter('unit'),
        onFilter: onFilter('unit'),
        filterDropdown,
        filterIcon,
      },
      {
        title: 'Remark',
        dataIndex: 'remark',
        sorter: sorter('remark'),
        onFilter: onFilter('remark'),
        filterDropdown,
        filterIcon,
      },
    ];

    if (permissions.includes(constants.permissions.updateStyle)) {
      column.push({
        title: 'Update',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteStyle)) {
      column.push({
        title: 'Delete',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 2200, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Style"
        moduleLink="/styles/list"
        subModuleName="List"
      />
      <div className={classes.tableStyle}>
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={styleList}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
    </>
  );
};

StyleList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(StyleList);
